import { useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import { Copy, Feedback } from "../icons";

const isUrl = (str) => /^https?:\/\//i.test(str);

const normalizeType = (rawType) => {
    if (!rawType || rawType === "text" || rawType === "document") return "document";
    if (String(rawType).startsWith("confluence")) return "confluence";
    return rawType;
};

const getCitationItems = (citations) => {
    if (!Array.isArray(citations)) return [];

    return citations
        .map((item) => {
            if (typeof item === "string") {
                const cleanPath = item.replace(/^\/+/, "");
                const label = cleanPath.split("/").pop() || cleanPath;
                return {
                    index: null,
                    href: isUrl(item) ? item : `download://${encodeURIComponent(cleanPath)}`,
                    path: isUrl(item) ? null : cleanPath,
                    label,
                    type: "document",
                };
            }

            if (item && typeof item === "object") {
                // New Citation shape: { source, title, type } or { index, source, title, type }
                if ("source" in item) {
                    const { index = null, source, title, type } = item;
                    const cleanPath = String(source || "").replace(/^\/+/, "");
                    return {
                        index,
                        href: isUrl(source) ? source : `download://${encodeURIComponent(cleanPath)}`,
                        path: isUrl(source) ? null : cleanPath,
                        label: title || cleanPath.split("/").pop() || cleanPath,
                        type: normalizeType(type),
                    };
                }

                // Legacy shape: { path, file, url, href, label, name }
                const rawPath = item.path || item.file || item.url || item.href || "";
                const cleanPath = String(rawPath).replace(/^\/+/, "");
                const label = item.label || item.name || cleanPath.split("/").pop() || cleanPath;

                if (!cleanPath) return null;

                return {
                    index: null,
                    href: isUrl(cleanPath) ? cleanPath : `download://${encodeURIComponent(cleanPath)}`,
                    path: isUrl(cleanPath) ? null : cleanPath,
                    label,
                    type: "document",
                };
            }

            return null;
        })
        .filter(Boolean);
};

const preprocessCitations = (text) => {
    if (!text) return "";
    return text.replace(/\[(\d+)\]/g, "`[$1]`");
};

const buildCopyText = (text, citationItems) => {
    const body = text || "";
    if (!Array.isArray(citationItems) || citationItems.length === 0) return body;

    const lines = citationItems.map((c, i) => {
        const num = c.index ?? i + 1;
        // External links expose a real URL in href; documents expose a file path.
        const link = c.path || (isUrl(c.href) ? c.href : "");
        return link ? `[${num}] ${c.label} — ${link}` : `[${num}] ${c.label}`;
    });

    return `${body}\n\nSources:\n${lines.join("\n")}`;
};

const normalizeActionName = (name) =>
    String(name || "")
        .trim()
        .toLowerCase()
        .replace(/[_\s]+/g, "-");

const toObject = (value) => {
    if (!value) return {};

    if (typeof value === "string") {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
        } catch {
            return {};
        }
    }

    return typeof value === "object" && !Array.isArray(value) ? value : {};
};

const labelFromActionName = (name) =>
    normalizeActionName(name)
        .split("-")
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");

const ACTION_LABEL_OVERRIDES = {
    "request-role": "Submit Role Request",
    "role-request": "Submit Role Request",
};

const labelForAction = (normalizedName, fallback) =>
    ACTION_LABEL_OVERRIDES[normalizedName] || fallback;

const normalizeFunctions = (functions) => {
    if (!Array.isArray(functions)) return [];

    return functions
        .map((fn) => {
            if (typeof fn === "string") {
                const normalizedName = normalizeActionName(fn);
                const isAllowedAction =
                    normalizedName === "reset" ||
                    normalizedName === "reset-case" ||
                    normalizedName === "escalate" ||
                    normalizedName === "escalate-case" ||
                    normalizedName === "request-role" ||
                    normalizedName === "role-request";

                if (!isAllowedAction) return null;

                return {
                    label: labelForAction(normalizedName, labelFromActionName(fn)),
                    name: normalizedName,
                    params: {},
                };
            }

            if (fn && typeof fn === "object") {
                const rawName = fn.name || fn.function_name || fn.label || "";
                const normalizedName = normalizeActionName(rawName);

                const isAllowedAction =
                    normalizedName === "reset" ||
                    normalizedName === "reset-case" ||
                    normalizedName === "escalate" ||
                    normalizedName === "escalate-case" ||
                    normalizedName === "request-role" ||
                    normalizedName === "role-request";

                if (!isAllowedAction) return null;

                const mergedParams = {
                    ...toObject(fn.payload),
                    ...toObject(fn.arguments),
                    ...toObject(fn.params),
                    ...toObject(fn.data),
                };

                [
                    "case_id",
                    "caseId",
                ].forEach((key) => {
                    if (fn[key] !== undefined && mergedParams[key] === undefined) {
                        mergedParams[key] = fn[key];
                    }
                });

                return {
                    label: labelForAction(normalizedName, fn.label || labelFromActionName(rawName)),
                    name: normalizedName,
                    params: mergedParams,
                };
            }

            return null;
        })
        .filter((fn) => fn?.name);
};

export default function WernerChat({
    messages = [],
    onSendMessage,
    isSendingMessage = false,
    onCitationClick,
    onMessageCopy,
    onFunctionCall,
    onFeedbackClick,
    disabledActionMessageIds = {},
    feedbackSubmittingMessageIds = {},
    submittedFeedbackMessageIds = {},
    disclaimerMessage,
    currentSpace,
}) {
    const inputRef = useRef(null);
    const bottomRef = useRef(null);

    const sendFromInput = () => {
        if (isSendingMessage) return;

        const value = inputRef.current?.value ?? "";
        if (!value.trim()) return;
        onSendMessage?.(value);
        inputRef.current.value = "";
        inputRef.current.focus();
    };

    const prevMessagesRef = useRef([]);

    useEffect(() => {
        const prev = prevMessagesRef.current;
        const curr = messages;

        const shouldScroll =
            curr.length > prev.length ||
            curr.some((msg, i) => prev[i]?.id !== msg.id);

        if (shouldScroll) {
            bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        }

        prevMessagesRef.current = curr;
    }, [messages]);

    const handleCitationLinkClick = (event, href) => {
        if (!href || !href.startsWith("download://")) return;

        event.preventDefault();
        const filePath = decodeURIComponent(href.replace("download://", ""));
        onCitationClick?.(filePath);
    };

    return (
        <div className="werner-chat">
            <div className="werner-chat-messages">
                {messages.map((m) => {
                    const isAi = m.sender === "ai";
                    const isLoading = Boolean(m.isLoading);
                    const spinnerLoader = isLoading && m.loadingVariant === "spinner";
                    const dotsLoader = isLoading && m.loadingVariant !== "spinner";
                    const citationItems = getCitationItems(m.citations);
                    const functionItems = normalizeFunctions(m.functions);
                    const isFeedbackSubmitting = Boolean(feedbackSubmittingMessageIds[m.id]);
                    const isFeedbackSubmitted = Boolean(submittedFeedbackMessageIds[m.id]);

                    return (
                        <div
                            key={m.id}
                            className={`werner-msg ${isAi ? "werner-msg--ai" : "werner-msg--human"}`}
                        >
                            {isAi ? (
                                <div className="werner-msg-aiText">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm, remarkBreaks]}
                                        components={{
                                            table: ({ children, ...props }) => (
                                                <div className="werner-md-tableWrap">
                                                    <table {...props}>{children}</table>
                                                </div>
                                            ),
                                            code: ({ inline, className, children, ...props }) => {
                                                if (inline) {
                                                    const citationMatch = String(children).match(/^\[(\d+)\]$/);
                                                    if (citationMatch) {
                                                        const idx = parseInt(citationMatch[1], 10);
                                                        const citation = citationItems.find((c) => c.index === idx);
                                                        return (
                                                            <sup className="werner-citation-ref">
                                                                <a
                                                                    href={citation?.href || "#"}
                                                                    target={citation?.path ? undefined : "_blank"}
                                                                    rel={citation?.path ? undefined : "noopener noreferrer"}
                                                                    title={citation?.label}
                                                                    onClick={(e) => handleCitationLinkClick(e, citation?.href)}
                                                                >
                                                                    {idx}
                                                                </a>
                                                            </sup>
                                                        );
                                                    }
                                                    return <code className="werner-md-inlineCode" {...props}>{children}</code>;
                                                }
                                                const match = /language-(\w+)/.exec(className || "");
                                                return (
                                                    <pre className="werner-md-pre">
                                                        <code
                                                            className={`werner-md-code ${match ? `lang-${match[1]}` : ""}`}
                                                            {...props}
                                                        >
                                                            {children}
                                                        </code>
                                                    </pre>
                                                );
                                            },
                                            a: ({ href, children, ...props }) => (
                                                <a
                                                    href={href}
                                                    {...props}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(event) => handleCitationLinkClick(event, href)}
                                                >
                                                    {children}
                                                </a>
                                            ),
                                        }}
                                    >
                                        {preprocessCitations(m.text || "")}
                                    </ReactMarkdown>

                                    {dotsLoader && (
                                        <div className="werner-msg-typing" aria-label="Werner is typing">
                                            <span className="werner-typing-dot" />
                                            <span className="werner-typing-dot" />
                                            <span className="werner-typing-dot" />
                                        </div>
                                    )}

                                    {spinnerLoader && (
                                        <div className="werner-msg-spinnerWrap" aria-label="Loading">
                                            <span className="werner-msg-spinner" />
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="werner-msg-bubble">
                                    <ReactMarkdown
                                        remarkPlugins={[remarkGfm, remarkBreaks]}
                                        components={{
                                            table: ({ children, ...props }) => (
                                                <div className="werner-md-tableWrap">
                                                    <table {...props}>{children}</table>
                                                </div>
                                            ),
                                            code: ({ inline, className, children, ...props }) => {
                                                const match = /language-(\w+)/.exec(className || "");
                                                return inline ? (
                                                    <code className="werner-md-inlineCode" {...props}>
                                                        {children}
                                                    </code>
                                                ) : (
                                                    <pre className="werner-md-pre">
                                                        <code
                                                            className={`werner-md-code ${match ? `lang-${match[1]}` : ""}`}
                                                            {...props}
                                                        >
                                                            {children}
                                                        </code>
                                                    </pre>
                                                );
                                            },
                                            a: ({ href, children, ...props }) => (
                                                <a
                                                    href={href}
                                                    {...props}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(event) => handleCitationLinkClick(event, href)}
                                                >
                                                    {children}
                                                </a>
                                            ),
                                        }}
                                    >
                                        {m.text || ""}
                                    </ReactMarkdown>
                                </div>
                            )}

                            {isAi && !isLoading && citationItems.length > 0 && (
                                <div className="werner-msg-citations">
                                    <br />

                                    <div className="werner-msg-citations-title">Sources</div>

                                    <ol className="werner-msg-citations-list">
                                        {citationItems.map((c) => (
                                            <li
                                                key={`${m.id}-citation-${c.index ?? c.label}`}
                                                value={c.index || undefined}
                                            >
                                                {c.type && c.type !== "document" && (
                                                    <span className="werner-citation-badge">{c.type}</span>
                                                )}
                                                <a
                                                    href={c.href}
                                                    target={c.path ? undefined : "_blank"}
                                                    rel={c.path ? undefined : "noopener noreferrer"}
                                                    onClick={(event) => {
                                                        if (c.path) {
                                                            event.preventDefault();
                                                            onCitationClick?.(c.path);
                                                        }
                                                    }}
                                                >
                                                    {c.label}
                                                </a>
                                            </li>
                                        ))}
                                    </ol>
                                </div>
                            )}

                            {isAi && !isLoading && functionItems.length > 0 && (
                                <div className="werner-msg-functions">
                                    {functionItems.map((fn, index) => {
                                        const isActionDisabled = Boolean(disabledActionMessageIds[m.id]);
                                        const normalizedName = normalizeActionName(fn.name);

                                        const modifierClass =
                                            normalizedName === "reset" || normalizedName === "reset-case"
                                                ? "werner-msg-functionBtn--reset"
                                                : normalizedName === "escalate" || normalizedName === "escalate-case"
                                                    ? "werner-msg-functionBtn--escalate"
                                                    : normalizedName === "request-role" || normalizedName === "role-request"
                                                        ? "werner-msg-functionBtn--requestRole"
                                                        : "";

                                        return (
                                            <button
                                                key={`${m.id}-fn-${index}`}
                                                type="button"
                                                className={`werner-msg-functionBtn ${modifierClass}`.trim()}
                                                onClick={() => onFunctionCall?.(fn.name, fn.params, m.text, m.id)}
                                                disabled={isActionDisabled}
                                            >
                                                {fn.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}

                            {isAi && !isLoading && (
                                <div className="werner-msg-actions">
                                    <button
                                        className="werner-msg-copy"
                                        type="button"
                                        onClick={() => onMessageCopy?.(buildCopyText(m.text, citationItems))}
                                        title="Copy"
                                        aria-label="Copy message"
                                    >
                                        <Copy className="werner-copy-icon" aria-hidden="true" />
                                    </button>

                                    <button
                                        className={`werner-msg-feedback ${isFeedbackSubmitted ? "is-submitted" : ""}`}
                                        type="button"
                                        onClick={() => onFeedbackClick?.(m)}
                                        title={
                                            isFeedbackSubmitted
                                                ? "Feedback sent"
                                                : isFeedbackSubmitting
                                                    ? "Sending feedback"
                                                    : "Send feedback"
                                        }
                                        aria-label={
                                            isFeedbackSubmitted
                                                ? "Feedback sent"
                                                : isFeedbackSubmitting
                                                    ? "Sending feedback"
                                                    : "Send feedback"
                                        }
                                        disabled={isFeedbackSubmitting || isFeedbackSubmitted}
                                    >

                                        <Feedback className="werner-feedback-icon" aria-hidden="true" />
                                    </button>
                                </div>
                            )}
                        </div>
                    );
                })}

                {disclaimerMessage && (
                    <div className="werner-chat-disclaimer">{disclaimerMessage}</div>
                )}

                <div ref={bottomRef} />
            </div>

            <div className="werner-chat-composer">
                <textarea
                    ref={inputRef}
                    className="werner-composer-input"
                    placeholder={`Type your message${currentSpace ? ` about ${currentSpace}` : ""}...`}
                    rows={1}
                    onKeyDown={(e) => {
                        if (isSendingMessage) {
                            e.preventDefault();
                            return;
                        }

                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            sendFromInput();
                        }
                    }}
                />

                <button
                    className="werner-composer-send"
                    type="button"
                    onClick={sendFromInput}
                    title="Send"
                    disabled={isSendingMessage}
                >
                    <span className="werner-send-icon" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
}